import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GET_PACKAGE_OFFER, type PackageOffer } from '../../graphql/queries/subscriptionPackages';
import { CREATE_PAYMENT_INTENT } from '../../graphql/mutations/payment';
import { resolveDiscountCode } from '../lib/discount-code';
import { useShell, type CheckoutCodes } from '../shell';
import { clearCheckoutIntent, saveCheckoutIntent } from './checkout-intent';

export type BuyStage = 'idle' | 'starting' | 'closed' | 'error';

/** What a direct checkout page shows: one of these, then the offer itself. */
export type BuyState = 'loading' | 'missing' | 'closed' | 'signed-out' | 'checkout' | 'error';

const NO_CODES: CheckoutCodes = { affiliateCode: () => null, userDiscountCode: async () => null };
const useNoCheckoutCodes = () => NO_CODES;

/**
 * Everything a direct checkout link (/buy/:packageCode) does, without the look, so a brand
 * can dress a campaign's page its own way (BuyView is the plain one). Signed in with the
 * offer on sale, it starts Stripe Checkout once by itself. Signed out, goToAuth remembers
 * the checkout (CheckoutIntentResumer comes back to it) and opens sign-up or sign-in.
 */
export function useBuyOffer(packageCode: string) {
  const shell = useShell();
  const viewer = shell.useViewer();
  const codes = (shell.useCheckoutCodes ?? useNoCheckoutCodes)();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [stage, setStage] = useState<BuyStage>('idle');
  const started = useRef(false);

  const { data, loading } = useQuery<{ getPackageOffer: PackageOffer | null }>(GET_PACKAGE_OFFER, {
    variables: { packageCode },
    fetchPolicy: 'network-only',
  });
  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);
  const offer = data?.getPackageOffer ?? null;
  const authLoading = viewer.authLoading ?? viewer.loading;

  const startCheckout = async (pkg: PackageOffer) => {
    setStage('starting');
    clearCheckoutIntent();
    try {
      const discountCode = resolveDiscountCode({
        fromUrl: searchParams.get('discount'),
        userAssigned: await codes.userDiscountCode(),
        promotion: codes.promotionCode,
      });
      const cycle = searchParams.get('cycle') === 'weekly' || !(pkg.priceMonthly > 0) ? 'WEEKLY' : 'MONTHLY';
      const { data: result } = await createPaymentIntent({
        variables: {
          input: {
            packageIds: [parseInt(pkg.id, 10)],
            billingCycle: cycle,
            paymentType: pkg.billingModel === 'ONE_TIME' ? 'ONE_OFF' : 'SUBSCRIPTION',
            affiliateCode: codes.affiliateCode(),
            discountCode,
          },
        },
      });
      const url = result?.createPaymentIntent?.checkoutUrl;
      if (!url) throw new Error('No checkout URL');
      shell.track('checkout_session_created', { pageType: 'buy_link', packageCode: pkg.packageCode });
      window.location.assign(url);
    } catch (error) {
      setStage(/SALE_CLOSED/.test(error instanceof Error ? error.message : '') ? 'closed' : 'error');
    }
  };

  // Signed in with an offer on sale: straight to Stripe, once.
  useEffect(() => {
    if (authLoading) return;
    if (!viewer.isAuthenticated || !offer?.onSale || started.current) return;
    started.current = true;
    void startCheckout(offer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.isAuthenticated, authLoading, offer]);

  const goToAuth = (path: '/register' | '/login') => {
    if (!offer) return;
    saveCheckoutIntent(offer.packageCode, location.search);
    navigate(path, { state: { from: location } });
  };

  const state: BuyState =
    loading || authLoading ? 'loading'
      : !offer ? 'missing'
        : !offer.onSale || stage === 'closed' ? 'closed'
          : !viewer.isAuthenticated ? 'signed-out'
            : stage === 'error' ? 'error'
              : 'checkout';

  return {
    state,
    offer,
    retry: () => { if (offer) void startCheckout(offer); },
    goToAuth,
  };
}
